import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { track } from '../../utils/track';
import TournamentFeeModal from './TournamentFeeModal';

/* This page load, for the game server's record of how a seat came back after an absence.
 * The id is minted once per load: a socket that reconnects within the same load presents
 * the same id (the connection blipped), a new load presents a new one — and `nav` says
 * whether that load was a reload. Safari on an iPhone kills and reloads a heavy page
 * (a white flash mid-match), which the server could not tell from a hidden tab or a lost
 * connection until it was told this. Never trusted for anything but the words on the
 * admin's match page. */
const PAGE_LOAD = (() => {
  const nav = (() => {
    try {
      const entry = performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;
      return entry?.type ?? null;
    } catch {
      return null;
    }
  })();
  const id = (() => {
    try {
      return crypto.randomUUID();
    } catch {
      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }
  })();
  return { id, nav };
})();

/*
 * ArenaGamePage — embeds the self-hosted INCRYPT Duel Arena game
 * (client/public/tcg-arena/index.html) inside the authenticated member
 * portal and wires the scoped game-session token flow.
 *
 * The Arena is a plain vanilla-JS/canvas game served from our own origin, so
 * it needs no CloudFront and the iframe points straight at the local asset.
 * The iframe is used purely to sandbox the game's global CSS, full-viewport
 * layout and lifecycle from the MUI portal — not for any engine requirement.
 * This component (the parent) owns the single authenticated Socket.IO
 * connection to tcg-game-server and relays a read-only view (session:ready +
 * live leaderboard) into the iframe. It intentionally does NOT drive
 * matchmaking or game moves — the server refuses client-authoritative play.
 *
 * With `guest` it is also the public demo (TradingCardGame › ArenaDemo): the same
 * arena and the same socket, on an anonymous practice-only session.
 */

const ARENA_GAME_URL =
  process.env.REACT_APP_TCG_ARENA_URL || '/tcg-arena/index.html';

/* What the guest frame may ask the host for: a practice match against the server's
 * bot, and to leave it. The game server enforces the same list for a tcg:guest token
 * (GUEST_EVENTS, GUEST_ONLY_PRACTICE); this keeps the host from even asking. */
const GUEST_ACTIONS = new Set(['core:bot', 'match:forfeit', 'match:cancel']);

/* The public demo's session, for the page that owns the invitation and the offer:
 * up, down, or not to be had (the mint was refused or could not be made). */
export type ArenaGuestStatus =
  | { type: 'connected' }
  | { type: 'disconnected' }
  | {
      type: 'unavailable';
      reason: 'busy' | 'maintenance' | 'network' | 'error';
      retryAfterSec?: number;
    };

type ArenaGamePageProps = {
  embedded?: boolean;
  requireAdmin?: boolean;
  // Override the embedded arena build. Defaults to the live/stable build. The
  // dev route (/member-portal/play-dev) passes nothing so it loads the HEAD build
  // (public/tcg-arena), while the member-facing /play route points at the promoted
  // stable snapshot (public/tcg-arena/stable).
  arenaUrl?: string;
  // Render a fixed "DEV BUILD" badge so the dev arena surface is never mistaken
  // for the member-facing play page.
  devTag?: boolean;
  /* THE PUBLIC DEMO. The session comes from POST /tcg-public/guest-session instead of
   * /tcg/session, and can only play practice against the server's bot. Nothing
   * member-shaped is fetched, pushed or relayed (no collection, decks, wallet,
   * leaderboard, packs, tutorial, social or watching); the frame loads the arena's
   * ?guest=1 mode; and nobody is redirected for not being signed in. */
  guest?: boolean;
  /* Guest only. Nothing is minted — and no socket opened — until this is above 0: a
   * page view is not a game, and the endpoint's per-IP limit is there for games. Each
   * increase is the visitor asking (again): mint if there is no session yet. */
  guestConnect?: number;
  // Guest only: the session's state (ArenaGuestStatus).
  onGuestStatus?: (status: ArenaGuestStatus) => void;
};

type GameSession = {
  gameSessionToken: string;
  gameServerUrl: string;
  expiresAt: number;
};

/* The guest session: the same shape as a member's (+ guest:true). A bare fetch with
 * credentials OMITTED, never the app's axios: the token names nobody, so the request
 * carries nobody — a signed-in member on the public page plays the demo as a guest
 * too. 429 = the per-IP limit (Retry-After), 503 = maintenance. */
type GuestSessionError = Error & { status?: number; retryAfterSec?: number };
const requestGuestSession = async (): Promise<GameSession> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/tcg-public/guest-session`,
    { method: 'POST', credentials: 'omit' }
  );
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    // the header when CORS exposes it, else the rate limiter's own body
    const retryAfter = Number(response.headers.get('Retry-After') ?? data?.retryAfter);
    const error: GuestSessionError = new Error(
      `Guest game session refused (${response.status})`
    );
    error.status = response.status;
    if (Number.isFinite(retryAfter) && retryAfter > 0) error.retryAfterSec = retryAfter;
    throw error;
  }
  if (!data?.gameSessionToken || !data?.gameServerUrl) {
    throw new Error('Game session response is incomplete.');
  }
  return {
    gameSessionToken: data.gameSessionToken,
    gameServerUrl: data.gameServerUrl,
    expiresAt: data.expiresAt
  };
};

const guestUnavailable = (error: unknown): ArenaGuestStatus => {
  const status = (error as GuestSessionError)?.status;
  return {
    type: 'unavailable',
    reason:
      status === 429 ? 'busy'
        : status === 503 ? 'maintenance'
          : error instanceof TypeError ? 'network'   // fetch could not reach the API at all
            : 'error',
    retryAfterSec: (error as GuestSessionError)?.retryAfterSec
  };
};

/* The INC balance as the portal header shows it (Header.tsx: tokenAmount + earnedTokens), as a
 * number; null when the member record carries neither (not loaded yet). */
const incBalanceOf = (u: { tokenAmount?: string; earnedTokens?: string } | null | undefined): number | null => {
  if (!u) return null;
  const held = parseFloat(u.tokenAmount ?? '');
  const earned = parseFloat(u.earnedTokens ?? '');
  if (!Number.isFinite(held) && !Number.isFinite(earned)) return null;
  const sum = (Number.isFinite(held) ? held : 0) + (Number.isFinite(earned) ? earned : 0);
  return sum >= 0 ? sum : null;
};

const ArenaGamePage = ({
  embedded = false,
  requireAdmin = true,
  arenaUrl,
  devTag = false,
  guest = false,
  guestConnect = 0,
  onGuestStatus
}: ArenaGamePageProps) => {
  const { isLoggedIn } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const socketRef = useRef<Socket | null>(null);
  // The game session token rotates every ~9 minutes, but it is only ever read at
  // the Socket.IO handshake. Holding it in a ref keeps it OUT of the socket
  // effect's deps: an established connection must survive a refresh (see below).
  const gameSessionTokenRef = useRef<string | null>(null);
  // the surface this page is (THE SURFACE, the socket handshake below): a prop that never changes
  // after mount, read through a ref so the socket effect's dependencies stay what they were
  const devTagRef = useRef(devTag);
  devTagRef.current = devTag;
  const lastServerStateRef = useRef<Record<string, unknown> | null>(null);
  /* The member's INC balance for the arena's lobby and settings (redesign brief §6a): the number
   * the portal header already shows (UserContext tokenAmount + earnedTokens) — never fetched for
   * the arena. Read through a ref so the socket effect, keyed to the server URL, pushes the latest
   * one with every state refresh as INCRYPT_TCG_SERVER_STATE.user.incBalance. A guest has none. */
  const incBalanceRef = useRef<number | null>(null);
  incBalanceRef.current = guest ? null : incBalanceOf(user);
  /* The tournament calls open for this member, keyed tournament:round:slot (the server's
   * tournament:call payloads; a call-ended removes one). The server re-sends an open call only
   * right after session:ready, which on a page load is while the iframe is still downloading
   * arena.js — so a call that arrived then was lost, and with nothing else offering ACCEPT a
   * reload during a call was a walkover. INCRYPT_TCG_READY replays them (sendFullStateToGame)
   * with the clock recomputed from the deadline. */
  const openCallsRef = useRef<Map<string, Record<string, unknown>>>(new Map());
  // Route Find Match to the server-authoritative core loop? Starts from the dev override
  // (?core=1 / localStorage), then the server's session:ready.coreLoop turns it on for
  // everyone once TCG_CORE_LOOP is set — one server flag, no client deploy. Held in a ref so
  // the socket effect's handlers read the latest value without re-subscribing.
  const coreModeRef = useRef<boolean>(
    new URLSearchParams(window.location.search).get('core') === '1' ||
      (() => { try { return localStorage.getItem('incrypt_core') === '1'; } catch { return false; } })()
  );
  // Static-ish per-session data the game needs (collection, decks, portal
  // wallet). Kept so we can resend if the iframe reloads.
  const staticMessagesRef = useRef<Record<string, unknown>[]>([]);
  const [gameSession, setGameSession] = useState<GameSession | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);
  // Set when the server bumps this socket because the same account connected on
  // another device/window (single-session enforcement).
  const [sessionReplaced, setSessionReplaced] = useState(false);
  /* A RELOADED PAGE is not dropped straight back onto the board. The server hands a live
   * core match to the socket the moment it connects — before session:ready, and usually
   * before the iframe has finished loading — so the board used to be posted into a frame
   * that was not yet listening and silently lost: the player landed on the menu with their
   * own match listed as live, while the server counted them back (the 2026-10-23 report).
   * Now a hand-over to a page that has never shown this board is HELD, the seat is told
   * away (the match stays paused on the absence budget, so nobody is skipped while a prompt
   * is up), and the player is asked. Reconnect relays the held board and tells the server
   * back, which re-sends the state with the clock re-anchored. */
  const [resumePrompt, setResumePrompt] = useState<{
    opponent: string | null;
    round: number | null;
    msLeft: number | null;
    at: number;
  } | null>(null);
  const [, setResumeTick] = useState(0);
  const [iframeReady, setIframeReady] = useState(false);
  const resumeRef = useRef<{ open: boolean; snapshot: unknown; battle: unknown }>({
    open: false,
    snapshot: null,
    battle: null
  });
  const resumeActRef = useRef<(() => void) | null>(null);
  /* THE ENTRY FEE (TournamentFeeModal): the game server answers a join with FEE_REQUIRED when
   * the tournament costs INC and this member has not paid. The arena cannot pay (no network
   * of its own), so the host opens the fee modal over it, and on a paid answer sends the
   * join the arena asked for (joinAfterPayRef, the socket effect's) and tells the arena to
   * re-fetch. */
  const [feePrompt, setFeePrompt] = useState<{ tournamentId: string; join: Record<string, unknown> } | null>(null);
  const joinAfterPayRef = useRef<((join: Record<string, unknown>) => void) | null>(null);
  // this page has shown a core board: a socket blip after that resumes silently, as before
  const seenBoardRef = useRef(false);
  /* The legacy door is closed and this build cannot be served by the core engine: the only way
     to play is a fresh load of the page (the current arena build). Set from the server's
     LEGACY_CLOSED answer; rendered as a screen with a Refresh button, like the takeover. */
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const legacyClosedRef = useRef(false);
  const [refreshRetryAt, setRefreshRetryAt] = useState<number | null>(null);
  // Guest only: read through a ref so a new callback each render never re-runs an effect.
  const onGuestStatusRef = useRef(onGuestStatus);
  onGuestStatusRef.current = onGuestStatus;
  // Guest only: the last ask (guestConnect) a mint was started for — one mint per ask.
  const guestAskRef = useRef(0);

  const baseArenaUrl = arenaUrl || ARENA_GAME_URL;
  const arenaGameUrl = guest
    ? baseArenaUrl + (baseArenaUrl.includes('?') ? '&' : '?') + 'guest=1'
    : baseArenaUrl;
  const gameOrigin = new URL(arenaGameUrl, window.location.origin).origin;

  const postToIframe = useCallback(
    (message: Record<string, unknown>) => {
      const target = iframeRef.current?.contentWindow;
      if (!target) return;
      target.postMessage(message, gameOrigin);
    },
    [gameOrigin]
  );

  // Full resend for when the iframe (re)loads with nothing: session token, the
  // last server state, and the static collection/decks/wallet messages.
  const sendFullStateToGame = useCallback(() => {
    // A guest's frame is sent none of this: it has no use for the token (the host
    // holds the socket), and no collection, decks or wallet to be sent.
    if (!gameSession || guest) return;
    postToIframe({ type: 'INCRYPT_TCG_SESSION', version: 1, ...gameSession });
    if (lastServerStateRef.current) {
      postToIframe(lastServerStateRef.current);
    }
    staticMessagesRef.current.forEach((message) => postToIframe(message));
    const now = Date.now();
    openCallsRef.current.forEach((call, key) => {
      const deadline = Date.parse(String(call.deadline ?? ''));
      if (!Number.isFinite(deadline) || deadline <= now) {
        openCallsRef.current.delete(key);
        return;
      }
      postToIframe({ ...call, msLeft: deadline - now, type: 'INCRYPT_TCG_TOURNAMENT', version: 1, event: 'call' });
    });
  }, [gameSession, guest, postToIframe]);

  // Session-token-only update, for when the token rotates (~every 9 min). It must
  // NOT resend the static collection/decks — the bridge's applyDecks re-seats the
  // deck slots from that push, which wiped a deck the player was mid-way through
  // building (the "deck reset one card before I could save" report). Nothing
  // static changes when only the token rotates, so there is nothing else to send.
  const sendSessionTokenToGame = useCallback(() => {
    if (!gameSession || guest) return;
    postToIframe({ type: 'INCRYPT_TCG_SESSION', version: 1, ...gameSession });
  }, [gameSession, guest, postToIframe]);

  const requestGameAccess = useCallback(
    async (isInitialRequest: boolean) => {
      if (!guest && (!isLoggedIn || (requireAdmin && user?.role !== 'admin'))) {
        return false;
      }

      if (isInitialRequest) {
        setIsRequestingAccess(true);
        setAccessError(null);
      }

      try {
        /* The public demo: an anonymous practice session, re-minted by the same timer
         * as a member's (below). A guest token names a RANDOM guest, so a re-mint is a
         * NEW guest: the live socket carries on as the old one (the token is read only
         * at the handshake), and only a reconnect after it arrives as the new one — to a
         * server with no practice match for it (session:ready below tells the arena, and
         * the demo deals a fresh one). Acceptable for a demo; a member's token keeps its
         * user, so none of this applies to them. */
        if (guest) {
          setGameSession(await requestGuestSession());
          setRefreshRetryAt(null);
          return true;
        }
        const requestSession = () =>
          // Mints the scoped tcg:play token for the self-hosted Duel Arena.
          // Auth rides the httpOnly cookie (withCredentials); no header to attach.
          axios.post(
            `${process.env.REACT_APP_API_URL}/tcg/session`,
            {},
            { withCredentials: true, withXSRFToken: true }
          );

        let response: Awaited<ReturnType<typeof requestSession>>;
        try {
          response = await requestSession();
        } catch (error) {
          if (!axios.isAxiosError(error) || error.response?.status !== 401) {
            throw error;
          }
          // Access cookie expired — refresh it (rotates the httpOnly cookie),
          // then retry.
          await axios.post(
            `${process.env.REACT_APP_API_URL}/auth/refresh-token`,
            {},
            { withCredentials: true, withXSRFToken: true }
          );
          response = await requestSession();
        }

        if (!response.data?.gameSessionToken || !response.data?.gameServerUrl) {
          throw new Error('Game session response is incomplete.');
        }

        setGameSession({
          gameSessionToken: response.data.gameSessionToken,
          gameServerUrl: response.data.gameServerUrl,
          expiresAt: response.data.expiresAt
        });
        setRefreshRetryAt(null);
        return true;
      } catch (error) {
        console.error('Failed to request arena game access:', error);
        if (isInitialRequest) {
          setAccessError('Unable to start the game session.');
          // the demo says so over its own frame (a guest never sees the screens below)
          if (guest) onGuestStatusRef.current?.(guestUnavailable(error));
        }
        return false;
      } finally {
        if (isInitialRequest) {
          setIsRequestingAccess(false);
        }
      }
    },
    [guest, isLoggedIn, requireAdmin, user?.role]
  );

  useEffect(() => {
    // Only the FIRST session request shows the loading screen. requestGameAccess
    // is a dep of this effect, so were it ever recreated we'd re-run and flip back
    // to the "Starting game session…" spinner — which unmounts and RELOADS the
    // arena iframe mid-play (the "page keeps refreshing" report). Guard on
    // gameSession so once we hold a session the iframe is never remounted; the
    // game-session token is renewed silently by the refresh effect below.
    // (With auth in an httpOnly cookie, portal-token refreshes no longer touch
    // React state, so they can't recreate requestGameAccess at all.)
    // The public demo mints when the visitor asks (guestConnect), once per ask.
    if (guest) {
      if (guestConnect > guestAskRef.current && !gameSession) {
        guestAskRef.current = guestConnect;
        requestGameAccess(true);
      }
      return;
    }
    if (!gameSession) requestGameAccess(true);
  }, [requestGameAccess, gameSession, guest, guestConnect]);

  // Refresh the short-lived game session token shortly before it expires.
  useEffect(() => {
    if (!gameSession) return;
    const tokenRefreshAt = gameSession.expiresAt * 1000 - 60_000;
    const refreshAt = refreshRetryAt || tokenRefreshAt;
    const delay = Math.max(1_000, refreshAt - Date.now());
    const refreshTimer = window.setTimeout(async () => {
      const renewed = await requestGameAccess(false);
      if (!renewed) setRefreshRetryAt(Date.now() + 30_000);
    }, delay);
    return () => window.clearTimeout(refreshTimer);
  }, [gameSession, refreshRetryAt, requestGameAccess]);

  /* Keep the handshake token current WITHOUT touching the connection.
   *
   * Socket.IO re-reads socket.auth on every (re)connect attempt, so mutating it
   * is enough: the live connection carries on, and any future reconnect presents
   * a fresh token. */
  useEffect(() => {
    gameSessionTokenRef.current = gameSession?.gameSessionToken ?? null;
    const socket = socketRef.current;
    if (socket && gameSession) {
      (socket.auth as { gameSessionToken: string }).gameSessionToken =
        gameSession.gameSessionToken;
    }
  }, [gameSession]);

  /* Tell the server when this tab is hidden — it cannot tell on its own.
   *
   * Hiding a tab stops requestAnimationFrame, and the Arena's whole game loop
   * runs on rAF, so the match stops being played the instant you switch tabs.
   * The socket does NOT stop: engine.io answers the server's ping from the
   * WebSocket packet handler, which background throttling cannot reach. Measured
   * in Chrome 143: a backgrounded tab ran zero frames for six minutes, answered
   * 52 pings and never dropped, while its timers were throttled to one per
   * minute. So from the server's side an away player looks perfectly healthy and
   * merely stops moving — which strands the match and voids it, taking their
   * opponent's win with it.
   *
   * visibilitychange is the one signal we can rely on here: it is not throttled,
   * and it fires before the freeze rather than after.
   *
   * The socket is deliberately NOT closed. Closing would work — it is the same
   * grace window either way — but it would also throw away the room membership
   * and force a full log rebuild for someone who tabbed away for four seconds.
   * The server pauses the match and starts their reconnect budget; coming back
   * bills the time and unpauses. */
  useEffect(() => {
    // Debounce the "away" signal. A MOMENTARY hide — an OS notification, a quick
    // focus flick, the tab backgrounded for a beat — should NOT flash "OPPONENT
    // DISCONNECTED" at the other player or start the forfeit clock. It only needs to
    // pause the match if we stay hidden past this window; a shorter hide self-heals
    // the instant focus returns (the game loop resumes and drains what it missed),
    // so the server never had to know. A genuine away (> window) still pauses as
    // before. match:back is sent only if we actually sent match:away.
    const AWAY_DEBOUNCE_MS = 2500;
    let awayTimer: ReturnType<typeof setTimeout> | null = null;
    let awaySent = false;
    const onVisibility = () => {
      const socket = socketRef.current;
      if (!socket?.connected) return;
      // the Reconnect prompt already holds this seat away (via 'reload'): a hide-and-show
      // meanwhile must not send the plain match:back that would resume the match and its
      // clock behind a prompt the player is still reading
      if (resumeRef.current.open) return;
      if (document.hidden) {
        if (awayTimer == null && !awaySent) {
          awayTimer = setTimeout(() => {
            awayTimer = null;
            const s = socketRef.current;
            if (document.hidden && s?.connected) {
              awaySent = true;
              s.emit('match:away');
            }
          }, AWAY_DEBOUNCE_MS);
        }
      } else {
        if (awayTimer != null) {
          clearTimeout(awayTimer);
          awayTimer = null;
        }
        if (awaySent) {
          awaySent = false;
          socket.emit('match:back');
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (awayTimer != null) clearTimeout(awayTimer);
    };
  }, []);

  /* Parent owns the single authenticated Socket.IO connection to the game server.
   *
   * Keyed to the server URL, NOT to gameSession. The token refresh replaces the
   * gameSession object every ~9 minutes, and depending on it here tore the socket
   * down and rebuilt it on every refresh. That was invisible while this socket
   * only carried leaderboard data, but it now carries match actions: rooms are
   * joined when a match starts, so the rebuilt socket was no longer in the room
   * and silently stopped receiving the opponent's moves — mid-match, every nine
   * minutes. The token is only read at the handshake (above), so a live socket has
   * no reason to care that it rotated. */
  const gameServerUrl = gameSession?.gameServerUrl ?? null;
  useEffect(() => {
    if (!gameServerUrl) return;

    /* SURFACE (owner 2026-11-04): which arena page this socket is on — 'dev' for /play-dev (the
     * HEAD build the admins test on), 'play' for the member-facing /play. The game server keeps
     * the two apart: who's-online rosters, challenges and the watch list never cross, so a dev
     * testing the next build is invisible to members on the live one and the other way round
     * (their games would be out of sync anyway — the queues are already keyed by ruleset). */
    const socket = io(gameServerUrl, {
      auth: { gameSessionToken: gameSessionTokenRef.current, page: PAGE_LOAD, surface: devTagRef.current ? 'dev' : 'play' },
      transports: ['websocket'],
      withCredentials: true
    });
    socketRef.current = socket;

    /* DEV-ONLY test hook. Exposes the live socket so the real-Chrome rig can force
     * a genuine reconnect (window.__arenaSocket.io.engine.close()) and exercise the
     * game:log rebuild — the one path only ArenaGamePage owns, which CDP setOffline
     * can't reach on a localhost WebSocket. Stripped from production builds:
     * process.env.NODE_ENV is inlined to 'production' there, so this branch is
     * dead-code-eliminated and window.__arenaSocket never exists live. Grants no new
     * capability even in dev — page script already holds the session token and could
     * open its own socket, so surfacing the existing one adds nothing. */
    if (process.env.NODE_ENV !== 'production') {
      (window as unknown as { __arenaSocket?: unknown }).__arenaSocket = socket;
    }

    let latestUser: unknown = null;
    let latestProfile: unknown = null;

    const publishState = (state: Record<string, unknown>) => {
      lastServerStateRef.current = state;
      postToIframe(state);
    };

    // Ask the server (authenticated) for the caller's own weekly standing, so
    // the game can show "your rank" even when outside the visible top 10.
    const fetchMyRank = () =>
      new Promise<unknown>((resolve) => {
        socket
          .timeout(5000)
          .emit('leaderboard:me', (err: Error | null, resp: any) => {
            resolve(!err && resp?.ok ? resp.me : null);
          });
      });

    // The most recent COMPLETED game week's top prize-winners, for the dev
    // arena menu's "last week's winners" panel. Public leaderboard endpoints, so
    // no auth: ask which week most recently closed, then that week's paying
    // places. Fetched once (it can't change mid-session) and then pushed with
    // every state update. Any failure just leaves the panel unshown — the stable
    // arena ignores this field entirely.
    // `settled` (the payout ran for that week; null = the ledger could not be read) and each
    // winner's `paid` / `paidTokens` (whole INC) are the game server's (00b7a61), from the
    // main API's payout ledger; passed on only when the server sent them, so an older server
    // leaves the arena's panel as it was. Payment is never inferred here.
    let lastWeekCache: {
      weekKey: string;
      settled?: boolean | null;
      winners: Array<{
        username: string;
        weeklyPoints: number;
        paid?: boolean | null;
        paidTokens?: number | null;
      }>;
    } | null = null;
    let lastWeekTried = false;
    const fetchLastWeek = async () => {
      if (lastWeekTried) return lastWeekCache;
      lastWeekTried = true;
      try {
        const wk = await axios.get(`${gameServerUrl}/leaderboard/weeks`, {
          params: { limit: 3 }
        });
        const weeks = (wk.data?.weeks || []) as Array<{
          weekKey: string;
          current?: boolean;
        }>;
        const prev = weeks.find((w) => !w.current);
        if (!prev) return null;
        const board = await axios.get(`${gameServerUrl}/leaderboard/weekly`, {
          params: { week: prev.weekKey, limit: 5 }
        });
        const winners = ((board.data?.entries || []) as Array<any>)
          .map((e) => ({
            username: String(e.username),
            weeklyPoints: Number(e.weeklyPoints),
            ...('paid' in e
              ? { paid: typeof e.paid === 'boolean' ? e.paid : null }
              : {}),
            ...('paidTokens' in e
              ? {
                  paidTokens:
                    e.paidTokens == null || !Number.isFinite(Number(e.paidTokens))
                      ? null
                      : Number(e.paidTokens)
                }
              : {})
          }))
          .filter((w) => w.username);
        const settledIn = board.data && typeof board.data === 'object' && 'settled' in board.data;
        lastWeekCache = winners.length
          ? {
              weekKey: prev.weekKey,
              ...(settledIn
                ? { settled: typeof board.data.settled === 'boolean' ? board.data.settled : null }
                : {}),
              winners
            }
          : null;
      } catch (error) {
        console.warn('Arena last-week winners fetch failed:', error);
        lastWeekCache = null;
      }
      return lastWeekCache;
    };

    // Fetch the weekly LP leaderboard we keep server-side and push it to the
    // game (it renders it in its native WEEKLY LEADERBOARD panel).
    const fetchAndPublish = async () => {
      let weekly: Record<string, unknown> = {};
      try {
        const res = await axios.get(`${gameServerUrl}/leaderboard/weekly`, {
          params: { limit: 10 }
        });
        weekly = res.data || {};
      } catch (error) {
        console.warn('Arena weekly leaderboard fetch failed:', error);
      }
      weekly.me = await fetchMyRank();
      const inc = incBalanceRef.current;
      publishState({
        type: 'INCRYPT_TCG_SERVER_STATE',
        connected: true,
        user:
          latestUser && typeof latestUser === 'object' && inc != null
            ? { ...(latestUser as Record<string, unknown>), incBalance: inc }
            : latestUser,
        profile: latestProfile,
        weekly,
        lastWeek: await fetchLastWeek()
      });
    };

    // Generic authenticated request/ack helper.
    const emitAck = (event: string, timeoutMs = 5000) =>
      new Promise<any>((resolve) => {
        socket
          .timeout(timeoutMs)
          .emit(event, (err: Error | null, resp: any) =>
            resolve(!err && resp?.ok ? resp : null)
          );
      });

    /* Pull the user's authoritative collection + decks + portal wallet from the
     * server and hand them to the game (source of truth = the server).
     *
     * RESILIENT, not one-shot (2026-10-19). This used to ask once, with a 5 s ceiling,
     * and give up until the next connect: a slow answer — a member with the whole
     * catalogue, a server just restarted — left the arena with an EMPTY collection,
     * every deck reading "EMPTY" or "INVALID DECK", and the deck builder showing no
     * cards, until they logged out and back in. Nothing was lost; the list never
     * arrived. Now the collection and the decks get a longer ceiling and are retried
     * with a growing wait; what has arrived is handed over at once, so a slow
     * collection does not hold the decks back. A later relay (a reconnect, a deck
     * save) starts over. */
    let relayRun = 0;
    const relayStaticData = async () => {
      const run = ++relayRun;
      const username = (latestUser as any)?.username;
      let collection: any = null, decks: any = null, wallet: any = null;
      const waits = [0, 2000, 5000, 10000];
      for (let attempt = 0; attempt < waits.length; attempt++) {
        if (waits[attempt]) await new Promise((r) => window.setTimeout(r, waits[attempt]));
        if (run !== relayRun || !socket.connected) return;   // superseded, or the socket went
        const [c, d, w] = await Promise.all([
          collection ? Promise.resolve(collection) : emitAck('collection:get', 15000),
          decks ? Promise.resolve(decks) : emitAck('decks:get', 15000),
          wallet ? Promise.resolve(wallet) : emitAck('wallet:portal:get', 5000)
        ]);
        if (run !== relayRun) return;
        // only what arrived in THIS attempt goes to the arena: re-posting a list it already has
        // would re-apply it over whatever the player is doing (a deck being built)
        const messages: Record<string, unknown>[] = [];
        if (c && !collection) messages.push({ type: 'INCRYPT_TCG_COLLECTION', username, cards: c.cards || [] });
        if (d && !decks) messages.push({ type: 'INCRYPT_TCG_DECKS', decks: d.decks || [], preferredDeckId: d.preferredDeckId ?? null });
        if (w?.walletAddress && !wallet) {
          messages.push({ type: 'INCRYPT_TCG_WALLET', walletAddress: w.walletAddress, verified: w.verified });
        }
        collection = c; decks = d; wallet = w;
        if (messages.length) {
          // kept BY TYPE: a partial attempt must not drop what an earlier one delivered, since
          // the kept set is replayed to the iframe whenever it is (re)mounted
          const kept = new Map<string, Record<string, unknown>>();
          for (const m of staticMessagesRef.current) kept.set(String(m.type), m);
          for (const m of messages) kept.set(String(m.type), m);
          staticMessagesRef.current = [...kept.values()];
          messages.forEach((message) => postToIframe(message));
        }
        if (collection && decks) return;
        console.warn('Arena: static data incomplete, retrying', { attempt: attempt + 1, collection: !!collection, decks: !!decks });
      }
      console.error('Arena: could not load the collection and decks after retries', { collection: !!collection, decks: !!decks });
    };

    /* The match this socket is playing, and the last action sequence we handed to
     * the game. Tracked so a reconnect can ask for exactly what it missed rather
     * than resuming out of sync. */
    let activeGameId: string | null = null;
    let lastSeq = -1;
    /* Whether this socket is PLAYING a core match. A core match has no match:found — its first
     * match:state IS its start — and the client is never told its id (by design), so it must
     * not borrow activeGameId, which is the relay game's (game:action, the log resume,
     * match:complete). Only the emote relay needs to know: live from the first match:state
     * until match:over, and dropped with the socket — a reconnect is re-pushed its state at
     * once, which sets it again. The server works out which core match an emote is for. */
    let coreMatchLive = false;
    // The connect-time hand-over of a live match arrives BEFORE session:ready (server.js);
    // after it, a match:state is a match this page queued into, or an ordinary push.
    let sessionReadyThisSocket = false;
    // Guest only: the connection dropped mid-match (see session:ready).
    let guestHadMatch = false;
    // ...and the void session:ready reports for it, held a moment in case the real ending comes
    let guestVoidTimer: number | null = null;
    /* And the match this socket is WATCHING, which is not the same thing and does not
     * survive a reconnect on its own: the server drops the watch when the socket goes,
     * so a reconnected socket is subscribed to nothing while the arena is still showing
     * a board and waiting for the next push. Remembered here so the reconnect can ask
     * for it again — see socket.on('connect') below. */
    let watchingGameId: string | null = null;
    /* Half of the last measured round-trip to the game server, from a spectate:join ack.
       A fight log says how far in the players were WHEN IT WAS SENT; by the time the arena
       reads it, it has crossed the network. The arena adds this back so a watcher does not
       start every fight one hop late. Bounded, and 0 until a join has been measured. */
    let lastOwdMs = 0;
    const noteRoundTrip = (sentAt: number) => {
      lastOwdMs = Math.max(0, Math.min(1500, Math.round((Date.now() - sentAt) / 2)));
    };
    /* Stamp a fight log with WHEN it reached this host (this machine's clock — the arena
       reads the same clock, so the hand-off through postMessage and whenArenaReady is
       measurable there) and the network hop before that. */
    const stampBattle = (battle: unknown) =>
      battle && typeof battle === 'object'
        ? { ...(battle as Record<string, unknown>), receivedAt: Date.now(), owdMs: lastOwdMs }
        : null;

    /* seqBaseline is the last action the game already has. A fresh match has
     * none (-1); a REBUILT one starts at the end of the log it just replayed, and
     * must say so here — the moves that arrived while we were rebuilding are
     * mostly moves the log already contained, and re-delivering one jams the
     * game's inbox for good: a duplicate from the round we are still in is not
     * stale (same round) and never becomes due (its effect is already applied),
     * so it blocks every move behind it and the round never advances to clear it. */
    const startMatch = (payload: Record<string, unknown>, seqBaseline = -1) => {
      activeGameId = (payload.gameId as string) ?? null;
      lastSeq = seqBaseline;
      postToIframe({ type: 'INCRYPT_TCG_MATCH_FOUND', version: 1, ...payload });
    };

    /* While we are rebuilding a rejoined match, live actions must WAIT.
     *
     * Rejoining puts us back in the room, so the opponent's moves start arriving
     * at once — while we are still fetching the log and replaying it. Applied
     * mid-rebuild they land on a half-built board; applied after, they may be
     * moves the log already contained. Either way the two clients quietly drift
     * apart, which is exactly what we rebuilt to avoid. So: hold them, then drain
     * whatever the log did not already cover. */
    let resuming = false;
    const pending: Record<string, unknown>[] = [];

    // Our own moves come back on the relay too (the server echoes to the room);
    // the game already applied them locally, so replaying them would double-apply.
    const applyRemoteAction = (payload: Record<string, unknown>) => {
      const seq = payload.seq as number;
      if (Number.isInteger(seq)) {
        if (seq <= lastSeq) return;          // already in the board we rebuilt
        lastSeq = seq;
      }
      const me = (latestUser as { id?: number } | null)?.id;
      if (me != null && payload.actorUserId === me) return;
      postToIframe({ type: 'INCRYPT_TCG_GAME_MSG', version: 1, action: payload.action });
    };

    const onRemoteAction = (payload: Record<string, unknown>) => {
      if (resuming) { pending.push(payload); return; }
      applyRemoteAction(payload);
    };

    socket.on('session:ready', async (payload: Record<string, unknown>) => {
      sessionReadyThisSocket = true;
      // a socket that reconnected while the prompt was up: the server counted the seat back
      // on connect (and re-sent the board, which is held); say away again, or the match runs
      // on behind a prompt the player is still reading
      if (resumeRef.current.open && !guest) socket.emit('match:away', { via: 'reload' });
      latestUser = payload?.user;
      latestProfile = payload?.profile;
      // If this is a RECONNECT and we were watching a match, ask for it again — the
      // server dropped the watch with the old socket. Defined further down; it does
      // nothing on a first connection. See rejoinWatch.
      rejoinWatch();
      // The server's cutover switch: once it has TCG_CORE_LOOP on, route Find Match to the
      // core loop for this client too (the dev ?core=1 override still wins if it was set).
      if (payload?.coreLoop) coreModeRef.current = true;
      // …and whether the legacy engine is closed to every build but the current one
      legacyClosedRef.current = payload?.legacyClosed === true;
      /* THE PUBLIC DEMO. A guest has no standing, collection, decks, wallet, friends or
       * live games (the server refuses each with GUEST_ONLY_PRACTICE), so none is asked
       * for: the session is up, and that is all there is to tell anyone. */
      if (guest) {
        // lifts the arena's own-connection freeze, as a member's state push does
        publishState({ type: 'INCRYPT_TCG_SERVER_STATE', connected: true });
        /* Back from a drop that was mid-match, and the server re-sent no match (it
         * re-sends a live one BEFORE session:ready): it has gone — out of reconnect
         * budget, or this is a re-minted token, i.e. a new guest (requestGameAccess).
         * Say so, or the arena sits frozen on a board nobody is playing; the demo
         * deals a fresh one on a void. */
        if (guestHadMatch && !coreMatchLive) {
          /* ...unless the server's own answer is on its way: a match it settled while we were
           * away (the reconnect budget ran out, a forfeit) is sent as match:over right AFTER
           * session:ready. So the void waits ~1.5 s, and a match:over calls it off (below):
           * inventing it at once gave the guest two endings for one match, the fake void and
           * then the real result. */
          if (guestVoidTimer != null) window.clearTimeout(guestVoidTimer);
          guestVoidTimer = window.setTimeout(() => {
            guestVoidTimer = null;
            if (coreMatchLive) return;           // a new match began meanwhile: never void that one
            postToIframe({
              type: 'INCRYPT_TCG_CORE_OVER',
              version: 1,
              result: { outcome: 'void', endReason: 'disconnect' }
            });
          }, 1500);
        }
        guestHadMatch = false;
        onGuestStatusRef.current?.({ type: 'connected' });
        return;
      }
      // Seed the who's-online panel with the list the server sent on connect.
      if (Array.isArray(payload?.online)) {
        postToIframe({ type: 'INCRYPT_TCG_SOCIAL', version: 1, event: 'presence', list: payload.online,
          meId: (payload.user as { id?: number } | undefined)?.id });
      }
      await fetchAndPublish();
      await relayStaticData();

      /* Mid-match when this socket connected — a refresh, or a dropped
       * connection. Rebuild it: the server hands back the same seed, seating and
       * deck it gave us originally, and game:log has every move since. The game
       * deals from the seed and fast-forwards the log to land on the board our
       * opponent is looking at.
       *
       * Fetch the WHOLE log (sinceSeq -1), not the part we missed: this socket is
       * new and its game is empty, so there is nothing to append to. */
      const resume = payload?.activeGame as Record<string, unknown> | null;
      if (resume) {
        const me = (latestUser as { id?: number } | null)?.id ?? null;
        const gameId = resume.gameId as string;
        activeGameId = gameId;
        resuming = true;                       // hold live moves until we've caught up
        try {
          const log = await new Promise<any>((resolve) => {
            socket
              .timeout(10_000)
              .emit('game:log', { gameId, sinceSeq: -1 },
                (err: unknown, res: unknown) => resolve(err ? null : res));
          });
          if (log?.ok) {
            startMatch({ ...resume, resumeLog: log.actions, youUserId: me },
              log.actions.reduce(
                (hi: number, a: { seq: number }) => Math.max(hi, a.seq), -1));
          } else {
            console.warn('Arena: mid-match but could not fetch the log', gameId, log?.code);
            activeGameId = null;
          }
        } finally {
          resuming = false;
          // anything that arrived while we rebuilt; applyRemoteAction drops what
          // the log already covered
          const held = pending.splice(0);
          for (const a of held) applyRemoteAction(a);
        }
      }
    });

    socket.on('match:found', (payload: Record<string, unknown>) => startMatch(payload));

    /* ── server-authoritative NEW CORE LOOP (tcg-game-server/docs/client-rework-plan.md) ──
     * The server runs the whole game and pushes per-seat state; the arena only renders it
     * and posts intents back (relayed in handleAction). No local game runs. */
    const closeResumePrompt = () => {
      const r = resumeRef.current;
      if (!r.open) return;
      r.open = false; r.snapshot = null; r.battle = null;
      setResumePrompt(null);
    };
    socket.on('match:state', (p: { snapshot?: unknown } = {}) => {
      coreMatchLive = true;
      const r = resumeRef.current;
      if (r.open) { r.snapshot = p.snapshot; return; }   // held until Reconnect
      if (!guest && !sessionReadyThisSocket && !seenBoardRef.current) {
        // the connect-time hand-over to a page that has never shown this board (a reload,
        // a new tab): hold it, tell the server away, and ask — see resumePrompt
        r.open = true; r.snapshot = p.snapshot; r.battle = null;
        socket.emit('match:away', { via: 'reload' });
        const snap = p.snapshot as
          | { opp?: { player?: { username?: string | null } }; round?: number }
          | undefined;
        setResumePrompt({
          opponent: snap?.opp?.player?.username ?? null,
          round: typeof snap?.round === 'number' ? snap.round : null,
          msLeft: null,
          at: Date.now()
        });
        return;
      }
      seenBoardRef.current = true;
      postToIframe({ type: 'INCRYPT_TCG_CORE_STATE', version: 1, snapshot: p.snapshot });
    });
    socket.on('match:battle', (p: { battle?: unknown } = {}) => {
      if (resumeRef.current.open) { resumeRef.current.battle = p.battle; return; }
      postToIframe({ type: 'INCRYPT_TCG_CORE_BATTLE', version: 1, battle: p.battle });
    });
    resumeActRef.current = () => {
      const r = resumeRef.current;
      if (!r.open) return;
      const { snapshot, battle } = r;
      r.open = false; r.snapshot = null; r.battle = null;
      seenBoardRef.current = true;
      setResumePrompt(null);
      // the held board first, so the arena is on it when the server's fresh state lands
      if (snapshot) postToIframe({ type: 'INCRYPT_TCG_CORE_STATE', version: 1, snapshot });
      if (battle) postToIframe({ type: 'INCRYPT_TCG_CORE_BATTLE', version: 1, battle });
      socket.emit('match:back', { via: 'reload' });
    };
    socket.on('match:over', (p: { result?: unknown } = {}) => {
      coreMatchLive = false;   // over: nothing left to react in
      closeResumePrompt();     // decided while the prompt was up (the budget ran out, a forfeit)
      // the real ending: a guest's invented void (session:ready) is not needed now
      if (guestVoidTimer != null) { window.clearTimeout(guestVoidTimer); guestVoidTimer = null; }
      postToIframe({ type: 'INCRYPT_TCG_CORE_OVER', version: 1, result: p.result });
    });
    socket.on('match:reject', (p: { snapshot?: unknown } = {}) => {
      if (p.snapshot) postToIframe({ type: 'INCRYPT_TCG_CORE_STATE', version: 1, snapshot: p.snapshot });
    });

    /* ── SPECTATING ──────────────────────────────────────────────────────────
     * Relayed straight through. A watcher has no seat, so none of this can reach a
     * game: the server refuses an intent from someone who is not playing, and this
     * page never sends one on their behalf. The snapshot arrives already normalised
     * to one shape (tcg-game-server src/game/spectate.js), so the arena renders it
     * without knowing which of the two games it is watching. */
    /* gameId rides through on every one of these. Without it the arena cannot tell
     * WHICH match a push is for, and a message still in flight when the watcher switches
     * matches lands on the new one — a board from one game, a fight from another, or a
     * 'MATCH OVER' for a game they have already left. */
    socket.on('spectate:state', (p: { gameId?: string; snapshot?: unknown } = {}) =>
      postToIframe({ type: 'INCRYPT_TCG_SPECTATE_STATE', version: 1, gameId: p.gameId, snapshot: p.snapshot }));
    socket.on('spectate:battle', (p: { gameId?: string; battle?: unknown } = {}) =>
      postToIframe({ type: 'INCRYPT_TCG_SPECTATE_BATTLE', version: 1, gameId: p.gameId, battle: stampBattle(p.battle) }));
    /* The watched match stands still (a player dropped) / moves again. `side`, never a
       user id: the arena names the player from the seating it already shows. */
    socket.on('spectate:paused', (p: { gameId?: string; side?: string; msRemaining?: number } = {}) =>
      postToIframe({ type: 'INCRYPT_TCG_SPECTATE_PAUSED', version: 1, gameId: p.gameId, side: p.side, msRemaining: p.msRemaining }));
    socket.on('spectate:resumed', (p: { gameId?: string; side?: string; msRemaining?: number } = {}) =>
      postToIframe({ type: 'INCRYPT_TCG_SPECTATE_RESUMED', version: 1, gameId: p.gameId, side: p.side, msRemaining: p.msRemaining }));
    socket.on('spectate:over', (p: { gameId?: string; result?: unknown } = {}) => {
      if (!p.gameId || p.gameId === watchingGameId) watchingGameId = null;
      postToIframe({ type: 'INCRYPT_TCG_SPECTATE_OVER', version: 1, gameId: p.gameId, result: p.result });
    });
    socket.on('spectate:ended', (p: { gameId?: string; reason?: string } = {}) => {
      // YOU_ARE_PLAYING is sent to the socket, not to a room, and carries no gameId
      if (!p.gameId || p.gameId === watchingGameId) watchingGameId = null;
      postToIframe({ type: 'INCRYPT_TCG_SPECTATE_ENDED', version: 1, gameId: p.gameId, reason: p.reason });
    });

    /* A RECONNECT resubscribes. socket.io reconnects on its own after a blip, but the
     * server dropped the watch when the old socket died — so without this the watcher
     * sits in front of a frozen board indefinitely, with nothing to say anything is
     * wrong. Fires on the first connection too, when there is nothing to rejoin.
     *
     * Hung off session:ready, NOT 'connect'. The server's connection handler awaits a
     * database lookup before it registers most of its listeners, so a join sent the
     * instant the transport is up arrives at a socket with no 'spectate:join' listener
     * and is dropped on the floor — no error, no ack, and the watch silently never
     * comes back. session:ready is emitted once that setup is done, so it is the
     * earliest moment the server can actually hear this. */
    const rejoinWatch = () => {
      const want = watchingGameId;
      if (!want) return;
      const sentAt = Date.now();
      socket.emit('spectate:join', { gameId: want },
        (res: { ok?: boolean; code?: string; gameId?: string; snapshot?: unknown; battle?: unknown; after?: unknown; paused?: unknown } = {}) => {
          noteRoundTrip(sentAt);
          if (res.ok) {
            /* Delivered as a JOIN, not as a state push: the reconnect may land mid-fight,
               in which case the snapshot is the board the fight STARTED from and `battle`
               is the fight to play over it, from where the players are. Sent as a bare
               STATE the arena rendered that starting board and sat on it — the fight, and
               the position, were dropped on the floor. */
            postToIframe({
              type: 'INCRYPT_TCG_SPECTATE_JOINED', version: 1, ok: true,
              gameId: res.gameId, snapshot: res.snapshot, battle: stampBattle(res.battle),
              after: res.after ?? null,   // with a battle: the state it lands in, held until the fight has played
              paused: res.paused ?? null
            });
            return;
          }
          // it ended, or is no longer watchable, while we were away: hand the arena back
          watchingGameId = null;
          postToIframe({
            type: 'INCRYPT_TCG_SPECTATE_ENDED', version: 1,
            gameId: want, reason: res.code || 'RECONNECT_FAILED'
          });
        });
    };

    /* Social pushes — presence + friend/challenge notifications — relayed to the
     * game menu. All cosmetic-to-the-match: none of these touch a live game. */
    const socialEvent =
      (event: string) =>
      (payload: Record<string, unknown> = {}) =>
        postToIframe({ type: 'INCRYPT_TCG_SOCIAL', version: 1, event, ...payload });
    socket.on('presence:list', (list: unknown) =>
      postToIframe({ type: 'INCRYPT_TCG_SOCIAL', version: 1, event: 'presence', list,
        meId: (latestUser as { id?: number } | null)?.id }));
    socket.on('friend:request-received', socialEvent('friend:request-received'));
    socket.on('friend:changed', socialEvent('friend:changed'));
    socket.on('challenge:received', socialEvent('challenge:received'));
    socket.on('challenge:declined', socialEvent('challenge:declined'));
    socket.on('challenge:expired', socialEvent('challenge:expired'));
    socket.on('challenge:cancelled', socialEvent('challenge:cancelled'));

    /* TOURNAMENTS (arena redesign step 5c; game server src/tournament/): the server's three
     * pushes, relayed to the arena's tournament card, bracket page and call popup as
     * INCRYPT_TCG_TOURNAMENT {event, ...payload}:
     *   call       {tournamentId, tournamentName, round, slot, roundName, rulesetVersion,
     *               opponent:{userId, username, seed}, you:{seed}, deadline, msLeft,
     *               accepted:{you, opp}} — on the call, on a reconnect, and whenever a seat
     *               accepts (a repeat for the same tournament/round/slot is an UPDATE)
     *   call-ended {tournamentId, round, slot, reason, winnerUserId}
     *   updated    {tournamentId} — re-fetch (the server coalesces these)
     * A guest's socket is never sent these (the server refuses tournaments to a guest); a
     * guest frame is not told of them either way. Tournament name and prize are admin text:
     * the arena writes them with textContent. */
    const tournamentEvent =
      (event: string) =>
      (payload: Record<string, unknown> = {}) => {
        if (guest) return;
        const key = `${payload.tournamentId}:${payload.round}:${payload.slot}`;
        if (event === 'call') openCallsRef.current.set(key, payload);
        else if (event === 'call-ended') openCallsRef.current.delete(key);
        postToIframe({ ...payload, type: 'INCRYPT_TCG_TOURNAMENT', version: 1, event });
      };
    socket.on('tournament:call', tournamentEvent('call'));
    socket.on('tournament:call-ended', tournamentEvent('call-ended'));
    socket.on('tournament:updated', tournamentEvent('updated'));
    // the check-in window opened (a paid tournament at its start time): the arena's popup
    socket.on('tournament:checkin', tournamentEvent('checkin'));
    joinAfterPayRef.current = (join) => {
      socket.timeout(10_000).emit('tournament:join', join, (err: Error | null, resp: any) => {
        // the arena hears the answer through its list: an 'updated' makes it re-fetch
        if (err || !resp?.ok) console.warn('Arena: the join after the fee was refused', err || resp);
        postToIframe({ type: 'INCRYPT_TCG_TOURNAMENT', version: 1, event: 'updated', tournamentId: join.tournamentId });
      });
    };

    socket.on('game:action', (payload: Record<string, unknown>) => onRemoteAction(payload));
    socket.on('match:error', (payload: Record<string, unknown>) => {
      console.warn('Arena matchmaking error:', payload?.code);
      // Tell the game, so a failed pairing (MATCHMAKING_FAILED) drops the player back
      // to the menu instead of leaving them stuck on the "searching…" overlay.
      postToIframe({ type: 'INCRYPT_TCG_MATCH_ERROR', version: 1, code: payload?.code });
    });

    // The opponent's emoji reaction — hand it to the game to display. Cosmetic only.
    socket.on('match:emote', (payload: Record<string, unknown>) => {
      postToIframe({ type: 'INCRYPT_TCG_EMOTE', version: 1, emoji: payload?.emoji });
    });

    /* A player dropped. The server freezes the match for BOTH sides while they
     * have reconnect budget left, so nothing is decided in their absence. It
     * knows who is connected; the game must never guess that for itself. */
    socket.on('match:paused', (payload: Record<string, unknown>) => {
      if (resumeRef.current.open && payload?.youArePaused === true) {
        const ms = typeof payload.msRemaining === 'number' ? payload.msRemaining : null;
        setResumePrompt((cur) => (cur ? { ...cur, msLeft: ms, at: Date.now() } : cur));
      }
      const me = (latestUser as { id?: number } | null)?.id;
      postToIframe({
        type: 'INCRYPT_TCG_MATCH_PAUSED',
        version: 1,
        ...payload,
        // legacy names the paused user; the core loop says it outright (no userId on the wire)
        youArePaused: typeof payload?.youArePaused === 'boolean'
          ? payload.youArePaused
          : me != null && payload?.userId === me
      });
    });
    socket.on('match:resumed', (payload: Record<string, unknown>) => {
      postToIframe({ type: 'INCRYPT_TCG_MATCH_RESUMED', version: 1, ...payload });
    });

    /* The server has decided the match — by replaying its log, or by forfeiting a
     * player who dropped. This is the authoritative word on it; the game may have
     * already drawn its own result screen from its local simulation, which is fine
     * (they agree), but this is the one that moved anyone's rating. */
    socket.on('match:ended', (payload: Record<string, unknown>) => {
      if (payload?.gameId === activeGameId) activeGameId = null;
      // Resolve "did I win?" here: the host knows who the player is, so the game
      // never needs to be told a user id to compare against. A bracket game's payload
      // carries `tournament` {tournamentId, name, round, roundName, rounds, nextRoundName,
      // applied, status, decidedBy, winnerUserId, recall, completed, champion} with
      // ranked:false: it rides through in the spread below (the match-end card's
      // tournament line).
      const me = (latestUser as { id?: number } | null)?.id;
      track('tcg.match_ended', {
        gameId: payload?.gameId,
        won: me != null && payload?.winnerUserId === me,
        endReason: payload?.endReason
      });
      postToIframe({
        type: 'INCRYPT_TCG_MATCH_ENDED',
        version: 1,
        ...payload,
        youWon: me != null && payload?.winnerUserId === me,
        // so the game can pick its own row out of payload.scores and show the RP
        // the server actually moved, instead of guessing
        youUserId: me ?? null
      });
      void fetchAndPublish();   // rating/leaderboard just moved
    });

    /* The game's moves, on their way to the server. Fire-and-forget, matching the
     * game's own Net.game() semantics — the server records and relays; who won is
     * decided later by replaying the log, never by anything said here. */
    const handleAction = (event: MessageEvent) => {
      if (event.origin !== gameOrigin) return;

      /* A guest plays one thing, a practice match against the bot: its intents go up,
       * and nothing else does — no relay game, no watching, no reactions to a bot. */
      if (guest) {
        if (event.data?.type === 'INCRYPT_TCG_CORE_INTENT') {
          socket.emit('core:intent', event.data.intent);
        }
        return;
      }

      /* The game thinks its match is over. Pass the nudge on — the server settles
       * by replaying its own log, so this asks it to look, it doesn't tell it
       * anything. If the log says the match is still running, the server ignores
       * us. */
      if (event.data?.type === 'INCRYPT_TCG_MATCH_OVER') {
        const gameId = event.data.gameId || activeGameId;
        if (gameId) socket.emit('match:complete', { gameId });
        return;
      }

      // Cosmetic emoji reaction — relay to the opponent. Not a game action: it never
      // touches the action log or the match result.
      if (event.data?.type === 'INCRYPT_TCG_EMOTE') {
        if (activeGameId) socket.emit('match:emote', { gameId: activeGameId, emoji: event.data.emoji });
        // a core match goes WITHOUT an id: the server knows which match this socket is in
        else if (coreMatchLive) socket.emit('match:emote', { emoji: event.data.emoji });
        return;
      }

      /* NEW CORE LOOP: the arena's player intent goes to the server, which validates and
       * pushes the next state. core:queue starts a server-authoritative match. */
      if (event.data?.type === 'INCRYPT_TCG_CORE_INTENT') {
        socket.emit('core:intent', event.data.intent);
        return;
      }
      if (event.data?.type === 'INCRYPT_TCG_CORE_QUEUE') {
        socket.emit('core:queue', { deckId: event.data.deckId, mode: event.data.mode });
        return;
      }

      /* Spectating. Each of these is answered on the socket's ack and posted back, so
       * the arena never waits on a push that may not come — a refusal (already in a
       * match, the game just ended) is an answer too. */
      if (event.data?.type === 'INCRYPT_TCG_SPECTATE_LIST') {
        socket.emit('spectate:list', {}, (res: { ok?: boolean; matches?: unknown[] } = {}) =>
          /* `ok` rides along so a REFUSED list is not mistaken for an EMPTY one. The
           * server rate-limits this to one call every two seconds, and a refusal was
           * being turned into [] here — so a second click wiped the panel and made it
           * look as though every game had just ended. */
          postToIframe({
            type: 'INCRYPT_TCG_SPECTATE_LIST',
            version: 1,
            ok: !!res.ok,
            code: (res as { code?: string }).code,
            matches: res.matches || []
          }));
        return;
      }
      if (event.data?.type === 'INCRYPT_TCG_SPECTATE_JOIN') {
        const sentAt = Date.now();
        socket.emit(
          'spectate:join',
          { gameId: event.data.gameId },
          (res: {
            ok?: boolean; code?: string; gameId?: string; snapshot?: unknown; battle?: unknown; after?: unknown; paused?: unknown;
          } = {}) => {
            noteRoundTrip(sentAt);
            watchingGameId = res.ok ? res.gameId ?? null : watchingGameId;
            postToIframe({
              type: 'INCRYPT_TCG_SPECTATE_JOINED',
              version: 1,
              ok: !!res.ok,
              code: res.code,
              gameId: res.gameId,
              snapshot: res.snapshot,
              /* Present only when they joined MID-FIGHT: the snapshot is then the board
                 the fight started from, and this is the fight to play over it — stamped
                 with when it got here and the hop it took, so the arena joins it where the
                 players are now, not where they were when the server answered. */
              battle: stampBattle(res.battle),
              /* With a battle: the state the fight lands in. The arena holds it until its fight
                 has played, as it holds the state that follows a pushed battle — without it a
                 mid-fight joiner sat on the pre-fight board until the next move. */
              after: res.after ?? null,
              // the match is standing still (a player dropped): freeze with it
              paused: res.paused ?? null
            });
          }
        );
        return;
      }
      if (event.data?.type === 'INCRYPT_TCG_SPECTATE_LEAVE') {
        watchingGameId = null;   // so a reconnect does not resubscribe to what they left
        socket.emit('spectate:leave', {});
        return;
      }

      if (event.data?.type !== 'INCRYPT_TCG_ACTION') return;
      if (!activeGameId) return;
      /* The game tags a 'place' with a client id (cid) so a placement the server
       * REFUSES can be undone in the game (revertPlace) instead of left lying on the
       * board. Strip it before the wire — the server never needs it — and echo it
       * back on a failed ack so the game hands the card back to the hand. */
      const action = (event.data.action || {}) as Record<string, unknown>;
      const { cid, ...wireAction } = action; // cid is client-only — strip it off the wire
      socket.emit(
        'game:action',
        { gameId: activeGameId, action: wireAction },
        (res: { ok?: boolean; code?: string } = {}) => {
          if (!res.ok) {
            console.warn('Arena action rejected:', res.code);
            if (cid != null) {
              postToIframe({ type: 'INCRYPT_TCG_ACTION_NAK', version: 1, cid, code: res.code });
            }
          }
        }
      );
    };
    window.addEventListener('message', handleAction);

    socket.on('connect_error', (error: Error) => {
      console.warn('Arena game server connection error:', error.message);
      publishState({ type: 'INCRYPT_TCG_SERVER_STATE', connected: false });
    });
    socket.on('disconnect', () => {
      if (guest && coreMatchLive) guestHadMatch = true;   // see session:ready
      coreMatchLive = false;   // the reconnect's re-pushed match:state sets it again, if it is still on
      publishState({ type: 'INCRYPT_TCG_SERVER_STATE', connected: false });
      if (guest) onGuestStatusRef.current?.({ type: 'disconnected' });
    });

    // The same account connected elsewhere and took over the session (the server
    // enforces one live socket per user). Show a takeover screen instead of the
    // game; the server-initiated disconnect that follows won't auto-reconnect.
    socket.on('session:replaced', () => {
      setSessionReplaced(true);
    });

    // Two-way channel: the game has no socket of its own, so it asks us to run
    // authenticated actions on its behalf. Only whitelisted actions are
    // forwarded, and the server re-validates everything regardless.
    const ALLOWED_ACTIONS = new Set([
      // match:forfeit = quitting a LIVE match (the bridge sends it on FORFEIT->MENU
      // from index.html). Omitting it here silently rejected every forfeit with
      // ACTION_NOT_ALLOWED, so the server was never told and the match ran on — the
      // "forfeit doesn't forfeit, it just loops" report. The server ignores it when
      // there is no live match, so allowing it is safe.
      // core:bot = practice against the server-side bot. The core rules live on the server,
      // so on the core loop the bot has to run there too; the arena asks for it and falls
      // back to its own local bot if we say no (see the coreLoop check below).
      // core:tutorial = the tutorial's scripted match, on the server engine for the same
      // reason; the arena plays its local lesson when we say no.
      'decks:save', 'decks:prefer', 'match:queue', 'match:cancel', 'match:forfeit', 'core:bot', 'core:tutorial',
      'friend:list', 'user:search', 'friend:request', 'friend:respond', 'friend:remove',
      'challenge:send', 'challenge:accept', 'challenge:decline',
      /* tournaments (step 5c): the list and one bracket (read-only; a get also subscribes
       * this socket to that bracket's tournament:updated), join with a deck (locks it),
       * leave, and accept a call. The arena's bridge adds rulesetVersion to join and accept;
       * the server checks every one of them. A guest never gets here (GUEST_ACTIONS). */
      'tournament:list', 'tournament:get', 'tournament:join', 'tournament:leave', 'tournament:accept',
      'tournament:checkin',
    ]);

    /* Finishing the tutorial pays real tokens, and that is the portal's business,
     * not the game server's — so this one goes to the member API over HTTP rather
     * than down the socket.
     *
     * The game does NOT get to say who is paid or how much. It reports one fact —
     * "I finished it" — and we report it as the signed-in user. The API decides
     * whether that is the first time and what it is worth, and it is the only
     * thing that can: the game's own record was localStorage, which is per browser
     * (clear it, or open another one, and you have never done the tutorial). */
    const callMemberApi = async (
      method: 'get' | 'post',
      path: string,
      opts?: { body?: unknown; raw?: boolean }
    ): Promise<any> => {
      const send = () =>
        axios.request({
          method,
          url: `${process.env.REACT_APP_API_URL}${path}`,
          data: method === 'post' ? opts?.body ?? {} : undefined,
          withCredentials: true,
          withXSRFToken: true
        });
      try {
        let response;
        try {
          response = await send();
        } catch (error) {
          if (!axios.isAxiosError(error) || error.response?.status !== 401) throw error;
          // Access cookie expired — refresh it (rotates the httpOnly cookie),
          // then retry. Auth rides the cookie; nothing comes back to JS.
          await axios.post(
            `${process.env.REACT_APP_API_URL}/auth/refresh-token`,
            {},
            { withCredentials: true, withXSRFToken: true }
          );
          response = await send();
        }
        return opts?.raw ? response.data : { ok: true, ...(response.data || {}) };
      } catch (error) {
        /* Say nothing rather than something wrong. The game only promises or
         * claims a reward on an explicit answer from the API, so a failure here
         * means the player is told they finished the tutorial and nothing about
         * tokens — which is the truth. The code this replaces fired a request and
         * told them they had been paid regardless of what came back. */
        console.warn(
          `Arena: member API call failed (${method.toUpperCase()} ${path}):`,
          axios.isAxiosError(error) ? error.message : error
        );
        if (opts?.raw) throw error;
        return { ok: false, code: 'MEMBER_API_FAILED' };
      }
    };

    /* Read-only, and write-once-server-side. Neither lets the game name a player
     * or an amount — it asks a question, or reports that it finished. */
    const MEMBER_API_ACTIONS: Record<string, ['get' | 'post', string]> = {
      'tcg:tutorial-status': ['get', '/tcg/tutorial-status'],
      'tcg:tutorial-complete': ['post', '/tcg/tutorial-complete'],
      /* What the weekly leaderboard pays. The menu used to hard-code it, and so
       * advertised a prize the payout would not honour the moment the amounts
       * changed. The config that pays is the only thing allowed to say. */
      'tcg:prize-pool': ['get', '/tcg/prize-pool']
    };

    const handleRequest = async (event: MessageEvent) => {
      if (event.origin !== gameOrigin) return;
      const data = event.data;
      if (data?.type !== 'INCRYPT_TCG_REQUEST') return;
      const { id, action, payload } = data;

      /* A guest asks for a practice match and to leave it — nothing else. Everything
       * member-shaped (the main API's /tcg/*, packs, the member relays, every other
       * socket event) is answered here without a call: there is no member to make it for. */
      if (guest && !GUEST_ACTIONS.has(action)) {
        postToIframe({ type: 'INCRYPT_TCG_RESPONSE', id, ok: false, code: 'GUEST' });
        return;
      }

      /* Core-loop dev toggle: send the arena's ordinary "Find Match" to the
       * server-authoritative path instead of the legacy matchmaker. The deck the player
       * chose (payload.deckId) and the mode ride straight through; core:queue acks just
       * like match:queue, so the arena's queued-lobby flow is unchanged. From here the
       * server pushes match:state and the arena renders it (renderSnapshot). */
      /* Answer core:bot here when the core loop is OFF, rather than making the arena wait
       * for a socket round-trip only to be refused: on the legacy build it should fall back
       * to its local bot immediately, exactly as it always did. core:tutorial the same: the
       * arena then plays its local lesson. */
      if ((action === 'core:bot' || action === 'core:tutorial') && !coreModeRef.current) {
        postToIframe({ type: 'INCRYPT_TCG_RESPONSE', id, ok: false, code: 'CORE_DISABLED' });
        return;
      }

      if (coreModeRef.current && action === 'match:queue') {
        const result = await new Promise<any>((resolve) => {
          socket
            .timeout(10_000)
            // rulesetVersion rides through too: the core protocol sends card ROW INDICES, so
            // two clients on different arena builds would render different cards from the same
            // snapshot. The server pools by mode + build and refuses one it cannot serve.
            .emit('core:queue',
              { deckId: payload?.deckId, mode: payload?.mode, rulesetVersion: payload?.rulesetVersion },
              (err: Error | null, resp: any) =>
                resolve(err ? { ok: false, code: 'REQUEST_TIMEOUT' } : resp));
        });
        /* Two refusals mean "this build cannot play a core match", not "no match for
         * you": RULESET_UNSUPPORTED (the arena embedded here is not the ruleset the core
         * serves — the stable /play snapshot before it is promoted, or a build still in
         * a member's cache after it) and CORE_DISABLED (the loop was switched off after
         * this tab connected, or the dev override is on against a server without it).
         * Either way the legacy matchmaker still serves this build, so the request
         * falls through to it below exactly as if the flag had never been on. Without
         * this, flipping TCG_CORE_LOOP ahead of the promote left every member unable to
         * find a match at all. Every other answer — queued, in a match, a bad deck — is
         * the core's to give, and is relayed as is. */
        const cannotServeThisBuild =
          result && result.ok === false &&
          (result.code === 'RULESET_UNSUPPORTED' || result.code === 'CORE_DISABLED');
        if (!cannotServeThisBuild) {
          postToIframe({ type: 'INCRYPT_TCG_RESPONSE', id, ...(result || { ok: false, code: 'REQUEST_FAILED' }) });
          return;
        }
        /* …unless the legacy door is closed: then there is nothing to fall back to, and the
           answer is a fresh load of the page — this tab is running an arena build the server
           no longer serves (a cached copy from before a promote). */
        if (result.code === 'RULESET_UNSUPPORTED' && legacyClosedRef.current) {
          postToIframe({ type: 'INCRYPT_TCG_RESPONSE', id, ok: false, code: 'LEGACY_CLOSED' });
          setNeedsRefresh(true);
          return;
        }
      }

      /* One tournament's live games (the bracket page's WATCH buttons, life and round): the
       * server's spectate:list filtered by {tournamentId}, asked for as a request and answered
       * on the response channel, so it never replaces the arena's own live list (that one is
       * INCRYPT_TCG_SPECTATE_LIST). Only with a tournament id: the unfiltered list keeps its
       * own message. */
      if (action === 'spectate:list') {
        const tournamentId = payload?.tournamentId;
        if (typeof tournamentId !== 'string' || !tournamentId) {
          postToIframe({ type: 'INCRYPT_TCG_RESPONSE', id, ok: false, code: 'ACTION_NOT_ALLOWED' });
          return;
        }
        const res = await new Promise<any>((resolve) => {
          socket
            .timeout(10_000)
            .emit('spectate:list', { tournamentId }, (err: Error | null, resp: any) =>
              resolve(err ? { ok: false, code: 'REQUEST_TIMEOUT' } : resp));
        });
        postToIframe({
          type: 'INCRYPT_TCG_RESPONSE',
          id,
          ok: !!res?.ok,
          code: res?.code,
          matches: Array.isArray(res?.matches) ? res.matches : []
        });
        return;
      }

      // Open the marketplace filtered to a card the player inspected in the arena — in a
      // NEW TAB (owner 2026-11-04), so the arena and whatever it is doing stay put. The
      // card rides in the query string (a new tab carries no router state); a blocked
      // popup falls back to navigating this tab, as before.
      if (action === 'tcg:open-marketplace') {
        const cardName =
          typeof payload?.cardName === 'string' ? payload.cardName : '';
        const url =
          '/member-portal/marketplace' +
          (cardName ? `?card=${encodeURIComponent(cardName)}` : '');
        // window.open with 'noopener' always returns null, which made the popup-blocked fallback
        // navigate the arena away as well (2026-11-05): open, then cut the opener by hand
        const tab = window.open(url, '_blank');
        if (tab) tab.opener = null;
        if (!tab) navigate(url);
        postToIframe({ type: 'INCRYPT_TCG_RESPONSE', id, ok: true });
        return;
      }

      // The player's unclaimed CARD packs — regular fulfilments + special packs,
      // flattened to { type, id, name }. Token and referral claims aren't cards,
      // so they're not offered here.
      if (action === 'tcg:packs-list') {
        try {
          const [regular, special] = await Promise.all([
            callMemberApi('get', '/fulfilment/check', { raw: true }),
            callMemberApi('get', '/special-pack-transactions/pending', { raw: true })
          ]);
          const packs: Array<{ type: string; id: number; name: string; starterLock?: { unlockAud: number } | null }> = [];
          (Array.isArray(regular) ? regular : []).forEach((p: any) =>
            packs.push({
              type: 'pack',
              id: p?.id,
              name: p?.Package?.name || p?.name || 'Card Pack',
              // the sign-up starter pack: opening it locks the account (server lib/starterLock)
              starterLock: p?.starterLock && Number(p.starterLock.unlockAud) > 0 ? { unlockAud: Number(p.starterLock.unlockAud) } : null
            })
          );
          (Array.isArray(special) ? special : []).forEach((p: any) =>
            packs.push({
              type: 'special-pack',
              id: p?.id,
              name: p?.specialPackage?.packageName || 'Special Pack'
            })
          );
          postToIframe({ type: 'INCRYPT_TCG_RESPONSE', id, ok: true, packs });
        } catch {
          postToIframe({ type: 'INCRYPT_TCG_RESPONSE', id, ok: false, code: 'PACKS_LIST_FAILED' });
        }
        return;
      }

      // Claim one card pack; hand back the cards it yielded (for the reveal). The
      // server is the authority on what's inside — the game never names a card.
      if (action === 'tcg:pack-claim') {
        try {
          const special = payload?.type === 'special-pack';
          const claimed = await callMemberApi(
            'post',
            special ? '/special-pack-transactions/claim' : '/fulfilment/complete-order',
            { raw: true, body: special ? { specialPackId: payload?.id } : { fulfilmentId: payload?.id } }
          );
          track('tcg.pack_claim', {
            special,
            packId: payload?.id,
            cards: ((claimed && claimed.selectedCards) || []).length
          });
          postToIframe({
            type: 'INCRYPT_TCG_RESPONSE',
            id,
            ok: true,
            cards: (claimed && claimed.selectedCards) || [],
            // the pull's share link (server lib/packPulls), for the reveal's SHARE button
            share: (claimed && claimed.share) || null
          });
        } catch {
          postToIframe({ type: 'INCRYPT_TCG_RESPONSE', id, ok: false, code: 'PACK_CLAIM_FAILED' });
        }
        return;
      }

      /* The arena's LADDER page (redesign step 4.2): this week and the last nine, the member
       * portal's weekly leaderboards page in the arena. The SAME main-API calls that page's
       * component (MemberPortal/Leaderboard) makes, in the same order: the recent weeks, then
       * each week's top 20 — this week first even before it has a score. A closed week's
       * paidTokens is what the payout ledger paid; `settled` says the week was paid. Asked by
       * the arena only while the page is open, at most once a minute; a week that fails comes
       * back ok:false (the arena says so) rather than looking empty. Read-only: the arena names
       * no player and no week the API did not hand us. */
      if (action === 'tcg:ladder') {
        try {
          const data = await callMemberApi('get', '/tcg/leaderboard/weeks?limit=10', { raw: true });
          const currentWeek: string = String(data?.currentWeek || '');
          const keys: string[] = [];
          const push = (k: unknown) => {
            const key = String(k || '');
            if (/^\d{8}$/.test(key) && !keys.includes(key)) keys.push(key);
          };
          push(currentWeek);
          ((data?.weeks || []) as Array<{ weekKey?: string }>).forEach((w) => push(w?.weekKey));
          const chosen = keys.slice(0, 10);
          const weeks = await Promise.all(
            chosen.map((k) =>
              callMemberApi('get', `/tcg/leaderboard/weekly?week=${k}&limit=20`, { raw: true })
                .then((r: { settled?: boolean; entries?: any[] }) => ({
                  weekKey: k,
                  current: k === currentWeek,
                  settled: !!r?.settled,
                  ok: true,
                  entries: (r?.entries || []).slice(0, 20).map((e: any) => ({
                    rank: Number(e?.rank),
                    username: e?.username == null ? null : String(e.username),
                    weeklyPoints: Number(e?.weeklyPoints) || 0,
                    weeklyWins: e?.weeklyWins == null ? null : Number(e.weeklyWins),
                    paidTokens: e?.paidTokens == null ? null : Number(e.paidTokens)
                  }))
                }))
                .catch(() => ({ weekKey: k, current: k === currentWeek, settled: false, ok: false, entries: [] }))
            )
          );
          postToIframe({ type: 'INCRYPT_TCG_RESPONSE', id, ok: true, currentWeek, weeks });
        } catch {
          postToIframe({ type: 'INCRYPT_TCG_RESPONSE', id, ok: false, code: 'LADDER_FAILED' });
        }
        return;
      }

      const memberCall = MEMBER_API_ACTIONS[action];
      if (memberCall) {
        const result = await callMemberApi(memberCall[0], memberCall[1]);
        postToIframe({ type: 'INCRYPT_TCG_RESPONSE', id, ...result });
        return;
      }

      if (!ALLOWED_ACTIONS.has(action)) {
        postToIframe({
          type: 'INCRYPT_TCG_RESPONSE',
          id,
          ok: false,
          code: 'ACTION_NOT_ALLOWED'
        });
        return;
      }

      const result = await new Promise<any>((resolve) => {
        socket
          .timeout(10_000)
          .emit(action, payload, (err: Error | null, resp: any) =>
            resolve(err ? { ok: false, code: 'REQUEST_TIMEOUT' } : resp)
          );
      });
      postToIframe({
        type: 'INCRYPT_TCG_RESPONSE',
        id,
        ...(result || { ok: false, code: 'REQUEST_FAILED' })
      });
      // The server refused the legacy engine outright (the door is closed): refresh is the
      // only way to play, so say so instead of leaving a refusal code on the lobby.
      if (result && result.ok === false && result.code === 'LEGACY_CLOSED') setNeedsRefresh(true);
      // a paid tournament: the fee first (the modal), then the join the arena asked for
      if (action === 'tournament:join' && result && result.ok === false && result.code === 'FEE_REQUIRED'
          && typeof payload?.tournamentId === 'string')
        setFeePrompt({ tournamentId: payload.tournamentId, join: { ...payload } });
      // Re-pull decks so the game reflects exactly what the server stored.
      if (result?.ok && action === 'decks:save') {
        track('tcg.deck_save');
        await relayStaticData();
      }
    };
    window.addEventListener('message', handleRequest);

    // Keep the leaderboard reasonably fresh while the arena is open (a guest has none).
    const refreshTimer = window.setInterval(() => {
      if (socket.connected && !guest) fetchAndPublish();
    }, 60_000);

    return () => {
      window.removeEventListener('message', handleRequest);
      window.removeEventListener('message', handleAction);
      window.clearInterval(refreshTimer);
      if (guestVoidTimer != null) window.clearTimeout(guestVoidTimer);   // no void into the next socket's arena
      resumeActRef.current = null;
      joinAfterPayRef.current = null;
      closeResumePrompt();   // a prompt belongs to the socket that raised it
      socket.removeAllListeners();
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
      if (process.env.NODE_ENV !== 'production') {
        const w = window as unknown as { __arenaSocket?: unknown };
        if (w.__arenaSocket === socket) w.__arenaSocket = null;
      }
    };
  }, [gameServerUrl, gameOrigin, postToIframe, guest]);

  // The session token rotated (~9 min). Update only the token in the iframe;
  // never resend static data here — that is what wiped in-progress decks.
  useEffect(() => {
    sendSessionTokenToGame();
  }, [sendSessionTokenToGame]);

  // The game announces readiness (INCRYPT_TCG_READY) once loaded; a fresh iframe
  // has nothing, so reply with the full state (session + server state + static).
  useEffect(() => {
    const receiveReady = (event: MessageEvent) => {
      if (event.origin !== gameOrigin) return;
      if (event.data?.type === 'INCRYPT_TCG_READY') {
        setIframeReady(true);   // the Reconnect prompt waits for this before it will relay a board
        sendFullStateToGame();
      }
    };
    window.addEventListener('message', receiveReady);
    return () => window.removeEventListener('message', receiveReady);
  }, [gameOrigin, sendFullStateToGame]);

  // The prompt's "reconnect within N s" counts down once a second while it is up.
  useEffect(() => {
    if (!resumePrompt) return;
    const t = window.setInterval(() => setResumeTick((x) => x + 1), 1000);
    return () => window.clearInterval(t);
  }, [resumePrompt]);

  // A guest is SUPPOSED to be signed out: the demo is for people without an account.
  if (!guest) {
    if (!isLoggedIn) return <Navigate to="/login" replace />;
    if (requireAdmin && user?.role !== 'admin') return <Navigate to="/" replace />;
  }

  // The arena build in this tab is no longer served and the legacy engine is closed: a fresh
  // load gets the current build. Shown once the server has said so (LEGACY_CLOSED).
  if (needsRefresh) {
    return (
      <Box
        sx={{
          width: embedded ? '100%' : '100vw',
          height: embedded ? '100%' : '100vh',
          minHeight: embedded ? '100%' : '100vh',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: '#05070c',
          color: '#ffffff',
          fontFamily: 'Montserrat, sans-serif',
          textAlign: 'center',
          px: 3
        }}
      >
        <Box sx={{ maxWidth: 420 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', mb: 1 }}>
            A new version of the arena is ready
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
            This page is running an older version of the game that the server no longer
            plays. Refresh to load the current one — your decks and cards are untouched.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ fontWeight: 700 }}
          >
            Refresh
          </Button>
        </Box>
      </Box>
    );
  }

  // Single-session takeover: this account is now playing on another device/window.
  if (sessionReplaced) {
    return (
      <Box
        sx={{
          width: embedded ? '100%' : '100vw',
          height: embedded ? '100%' : '100vh',
          minHeight: embedded ? '100%' : '100vh',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: '#05070c',
          color: '#ffffff',
          fontFamily: 'Montserrat, sans-serif',
          textAlign: 'center',
          px: 3
        }}
      >
        <Box sx={{ maxWidth: 420 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', mb: 1 }}>
            Playing on another device
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
            This game was opened in another window or on another device, so it was
            closed here. You can only play one session at a time.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ fontWeight: 800, backgroundColor: '#38e8ff', color: 'white' }}
          >
            Play here instead
          </Button>
        </Box>
      </Box>
    );
  }

  // A guest's frame is up from the start: the arena is its own loading screen, and the
  // demo's panels sit over it until there is a session (guestConnect).
  if (!guest && (isRequestingAccess || !gameSession)) {
    return (
      <Box
        sx={{
          width: embedded ? '100%' : '100vw',
          height: embedded ? '100%' : '100vh',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: '#05070c',
          color: '#ffffff',
          fontFamily: 'Montserrat, sans-serif',
          minHeight: embedded ? '100%' : '100vh'
        }}
      >
        {accessError ? (
          <Typography sx={{ fontWeight: 800 }}>{accessError}</Typography>
        ) : (
          <Box sx={{ display: 'grid', placeItems: 'center', gap: 2 }}>
            <CircularProgress sx={{ color: '#38e8ff' }} />
            <Typography sx={{ fontWeight: 800 }}>
              Starting game session...
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: embedded ? '100%' : '100vh',
        /* embedded: the portal's .arena-fade--game is a flex column, so flex sizing carries the
           height too — belt and braces for a percentage that one engine resolves differently */
        flex: embedded ? '1 1 auto' : undefined,
        minHeight: embedded ? 0 : undefined,
        backgroundColor: '#000',
        overflow: 'hidden'
      }}
    >
      {devTag && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            px: 1.5,
            py: 0.25,
            borderBottomLeftRadius: 6,
            borderBottomRightRadius: 6,
            backgroundColor: '#f0a020',
            color: '#1a1200',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 800,
            fontSize: 12,
            letterSpacing: 1,
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
            '& .dev-short': { display: 'none' },
            // a phone (upright, or on its side): a thin tag on the left edge, half-way down, in the
            // gutter beside the board — the top-centre badge covered the opponent's name there
            '@media (max-width: 700px), (max-height: 560px)': {
              top: '50%',
              left: 0,
              transform: 'translateY(-50%) rotate(180deg)',
              writingMode: 'vertical-rl',
              px: 0.25,
              py: 1,
              fontSize: 9,
              letterSpacing: 2,
              borderRadius: 0,
              borderTopLeftRadius: 4,
              borderBottomLeftRadius: 4,
              opacity: 0.85,
              '& .dev-long': { display: 'none' },
              '& .dev-short': { display: 'inline' }
            }
          }}
        >
          <span className="dev-long">DEV BUILD — not the live play page</span>
          <span className="dev-short" aria-hidden="true">
            DEV BUILD
          </span>
        </Box>
      )}
      {feePrompt && (
        <TournamentFeeModal
          tournamentId={feePrompt.tournamentId}
          onClose={() => setFeePrompt(null)}
          onPaid={() => {
            const join = feePrompt.join;
            setFeePrompt(null);
            joinAfterPayRef.current?.(join);
          }}
        />
      )}
      {resumePrompt && (() => {
        const secs =
          resumePrompt.msLeft == null
            ? null
            : Math.max(0, Math.round((resumePrompt.msLeft - (Date.now() - resumePrompt.at)) / 1000));
        return (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 20,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: 'rgba(5, 7, 12, 0.9)',
              color: '#ffffff',
              fontFamily: 'Montserrat, sans-serif',
              textAlign: 'center',
              px: 3
            }}
          >
            <Box sx={{ maxWidth: 420 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', mb: 1 }}>
                You have a match in progress
              </Typography>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
                {resumePrompt.opponent
                  ? `You were playing ${resumePrompt.opponent}`
                  : 'Your match is still on'}
                {resumePrompt.round ? `, round ${resumePrompt.round}` : ''}. The clock is paused
                while you are away. Reconnect{secs != null ? ` within ${secs} s` : ' now'}, or the
                match is forfeited.
              </Typography>
              <Button
                variant="contained"
                disabled={!iframeReady}
                onClick={() => resumeActRef.current?.()}
                sx={{ fontWeight: 700 }}
              >
                {iframeReady ? 'Reconnect' : 'Loading the arena…'}
              </Button>
            </Box>
          </Box>
        );
      })()}
      <iframe
        ref={iframeRef}
        title="INCRYPT Trading Card Game"
        src={arenaGameUrl}
        allow="fullscreen; gamepad; autoplay; clipboard-write"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        onLoad={sendFullStateToGame}
        style={{
          width: '100%',
          height: '100%',
          border: 0,
          display: 'block',
          backgroundColor: '#000'
        }}
      />
    </Box>
  );
};

export default ArenaGamePage;
